import { KText } from '@/ui-library/components';
import {
  AssociatedRequisitions,
  DepartmentName,
  HiringManagers,
  JobClosedBy,
  JobCreatedBy,
  Jobname,
  OfficeNames,
  Recruiters,
  TimeToClose,
  WorkType,
  Workplace,
} from '../cells/JobDrilldownCells';

const renderDrilldownHeader = (title: string) => () => (
  <KText size="sm-medium" color="secondary-900" textTransform="none">
    {title}
  </KText>
);

export const useJobDrilldownConfig = ({
  drilldownHeading = 'Jobs opened',
  onSort: _onSort = () => {},
  currentSort: _currentSort = {},
}) => {
  const getColumnConfig = () => {
    const columnsMap = {
      jobName: {
        title: 'Job name',
        Header: renderDrilldownHeader('Job name'),
        id: 'job_name',
        minWidth: 200,
        accessor: 'ats_job',
        columnConfig: false,
        sort: true,
        Cell: ({ row }) => Jobname(row),
      },
      recruiters: {
        title: 'Recruiters',
        Header: renderDrilldownHeader('Recruiters'),
        minWidth: 200,
        accessor: 'recruiters',
        sort: true,
        Cell: ({ row }) => Recruiters(row),
      },
      hiringManagers: {
        title: 'Hiring managers',
        Header: renderDrilldownHeader('Hiring managers'),
        minWidth: 200,
        accessor: 'hiring_managers',
        sort: true,
        Cell: ({ row }) => HiringManagers(row),
      },
      department: {
        title: 'Department',
        Header: renderDrilldownHeader('Department'),
        id: 'department_name',
        minWidth: 200,
        accessor: 'ats_department',
        sort: true,
        Cell: ({ row }) => DepartmentName(row),
      },
      office: {
        title: 'Offices',
        Header: renderDrilldownHeader('Offices'),
        id: 'office_name',
        minWidth: 220,
        accessor: 'office_names',
        sort: true,
        Cell: ({ row }) => OfficeNames(row),
      },
      timeToClose: {
        title: 'Time to close',
        Header: renderDrilldownHeader('Time to close'),
        minWidth: 200,
        accessor: 'time_to_close',
        sort: true,
        Cell: ({ row }) => TimeToClose(row),
      },
      workplace: {
        title: 'Workplace',
        Header: renderDrilldownHeader('Workplace'),
        minWidth: 160,
        accessor: 'workplace',
        sort: true,
        Cell: ({ row }) => Workplace(row),
      },
      workType: {
        title: 'Work type',
        Header: renderDrilldownHeader('Work type'),
        minWidth: 160,
        accessor: 'work_type',
        sort: true,
        Cell: ({ row }) => WorkType(row),
      },
      associatedRequisitions: {
        title: 'Associated requisitions',
        Header: renderDrilldownHeader('Associated requisitions'),
        minWidth: 220,
        accessor: 'associated_requisitions',
        sort: true,
        Cell: ({ row }) => AssociatedRequisitions(row),
      },
      jobCreatedBy: {
        title: 'Job created by',
        Header: renderDrilldownHeader('Job created by'),
        minWidth: 180,
        accessor: 'job_created_by',
        sort: true,
        Cell: ({ row }) => JobCreatedBy(row),
      },
      jobClosedBy: {
        title: 'Job closed by',
        Header: renderDrilldownHeader('Job closed by'),
        minWidth: 180,
        accessor: 'closed_by',
        sort: true,
        Cell: ({ row }) => JobClosedBy(row),
      },
    };

    const commonRecruitersToJobCreatedBy = [
      columnsMap.recruiters,
      columnsMap.hiringManagers,
      columnsMap.department,
      columnsMap.office,
      columnsMap.workplace,
      columnsMap.workType,
      columnsMap.associatedRequisitions,
      columnsMap.jobCreatedBy,
    ];

    switch (drilldownHeading) {
      case 'Jobs opened':
        return [columnsMap.jobName, ...commonRecruitersToJobCreatedBy];

      case 'Jobs closed':
        return [
          columnsMap.jobName,
          columnsMap.timeToClose,
          columnsMap.jobClosedBy,
          columnsMap.recruiters,
          columnsMap.hiringManagers,
          columnsMap.department,
          columnsMap.office,
          columnsMap.workplace,
          columnsMap.workType,
          columnsMap.associatedRequisitions,
          columnsMap.jobCreatedBy,
        ];

      case 'Time to close':
        return [
          columnsMap.jobName,
          columnsMap.timeToClose,
          columnsMap.jobClosedBy,
          ...commonRecruitersToJobCreatedBy,
        ];

      case 'Target date breached jobs':
        return [columnsMap.jobName, ...commonRecruitersToJobCreatedBy];

      case 'Job open trend':
        return [
          columnsMap.jobName,
          columnsMap.recruiters,
          columnsMap.hiringManagers,
          columnsMap.department,
          columnsMap.office,
          columnsMap.workType,
          columnsMap.associatedRequisitions,
          columnsMap.jobCreatedBy,
        ];

      case 'Jobs opened by location':
        return [
          columnsMap.jobName,
          columnsMap.recruiters,
          columnsMap.hiringManagers,
          columnsMap.department,
          columnsMap.workplace,
          columnsMap.workType,
          columnsMap.associatedRequisitions,
          columnsMap.jobCreatedBy,
        ];

      case 'Jobs opened by department':
        return [
          columnsMap.jobName,
          columnsMap.recruiters,
          columnsMap.hiringManagers,
          columnsMap.office,
          columnsMap.workplace,
          columnsMap.workType,
          columnsMap.associatedRequisitions,
          columnsMap.jobCreatedBy,
        ];

      case 'Jobs closed without hires':
        return [
          columnsMap.jobName,
          columnsMap.timeToClose,
          columnsMap.jobClosedBy,
          ...commonRecruitersToJobCreatedBy,
        ];

      default:
        return [columnsMap.jobName, ...commonRecruitersToJobCreatedBy];
    }
  };

  return { columnConfig: getColumnConfig() };
};
